import { useCallback, useEffect, useRef, useState } from 'react'
import { getImportProgress } from '../services/ShipmentBulkUploadServices'
import { ImportProgressDTO } from '../interfaces/Import/ShipmentBulkUploadApi'

/**
 * Polling del progreso de una carga masiva (spec §6.3), endurecido contra los 2
 * CRITICAL GAP del review:
 *
 *  1. Red caída → NO congela la barra en silencio. Mantiene el último progreso
 *     conocido y expone `connectionLost` (la vista muestra banner "Reconectando…")
 *     mientras sigue reintentando con backoff exponencial (2s → 4s → … → 30s),
 *     para no martillar un backend caído (thundering herd).
 *  2. Pausa cuando la pestaña pierde foco (document.visibilityState) y reanuda al
 *     volver, para no quemar batería / requests en background.
 *
 * Termina (deja de pollear) cuando is_complete, o cuando el header no existe
 * (code shipping.ShipmentBulkUploadHeader.1 → `notFound`). Limpia el timer al desmontar.
 *
 * El auto-reintento de errores transitorios POR DETALLE (#2) NO vive acá: necesita la
 * lista de detalles (getImport) para clasificar; lo orquesta la vista de detalle con
 * classifyImportError + executeImport + un cap de reintentos.
 */

const NOT_FOUND_CODE = 'shipping.ShipmentBulkUploadHeader.1'
const MAX_BACKOFF_MS = 30000

export interface UseImportProgressOptions {
  /** Intervalo base entre polls (ms). Default 2000. */
  intervalMs?: number
  /** Si false, no pollea (ej: header ya finalizado al entrar). Default true. */
  enabled?: boolean
  /** Fallos consecutivos antes de marcar connectionLost. Default 1. */
  failThreshold?: number
  /** Callback cuando is_complete pasa a true. */
  onComplete?: (progress: ImportProgressDTO) => void
  /**
   * Cambiar este número reinicia el polling desde cero (ej: tras un executeImport
   * que reabre el header a InProcess). Sin cambios, no reinicia.
   */
  restartSignal?: number
}

export interface UseImportProgressResult {
  progress: ImportProgressDTO | null
  /** Carga inicial (aún sin primer dato). */
  loading: boolean
  /** Hubo >= failThreshold fallos seguidos; se sigue reintentando con backoff. */
  connectionLost: boolean
  isComplete: boolean
  /** El header no existe / fue eliminado (terminal, deja de pollear). */
  notFound: boolean
  error: string | null
  /** Fuerza un refetch inmediato (ej: tras cancelar/eliminar/reintentar). */
  refresh: () => void
}

export function useBulkUploadProgress(
  headerId: string | null | undefined,
  options: UseImportProgressOptions = {}
): UseImportProgressResult {
  const {
    intervalMs = 2000,
    enabled = true,
    failThreshold = 1,
    onComplete,
    restartSignal,
  } = options

  const [progress, setProgress] = useState<ImportProgressDTO | null>(null)
  const [loading, setLoading] = useState<boolean>(!!headerId && enabled)
  const [connectionLost, setConnectionLost] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const failCountRef = useRef(0)
  const stoppedRef = useRef(false)
  const mountedRef = useRef(true)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!headerId || !enabled) {
      setLoading(false)
      return
    }

    let cancelled = false
    stoppedRef.current = false
    failCountRef.current = 0
    setLoading(true)
    setIsComplete(false)
    setNotFound(false)
    setError(null)

    const scheduleNext = (delay: number) => {
      clearTimer()
      timerRef.current = setTimeout(tick, delay)
    }

    const tick = async () => {
      if (cancelled || !mountedRef.current || stoppedRef.current) return
      // Pausa en background: no fetch, reintentar al intervalo base.
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        scheduleNext(intervalMs)
        return
      }

      const res = await getImportProgress(headerId)
      if (cancelled || !mountedRef.current || stoppedRef.current) return
      setLoading(false)

      if (res.didError) {
        if (res.code === NOT_FOUND_CODE) {
          setNotFound(true)
          setError(res.errorMessage || 'La carga masiva no existe o fue eliminada')
          stoppedRef.current = true // terminal
          return
        }
        // Red/transitorio: conservar último progreso, marcar reconexión, seguir con backoff.
        failCountRef.current += 1
        if (failCountRef.current >= failThreshold) setConnectionLost(true)
        const backoff = Math.min(
          intervalMs * 2 ** (failCountRef.current - 1),
          MAX_BACKOFF_MS
        )
        scheduleNext(backoff)
        return
      }

      // Éxito.
      failCountRef.current = 0
      setConnectionLost(false)
      const data = res.model
      if (data) {
        setProgress(data)
        if (data.is_complete) {
          setIsComplete(true)
          stoppedRef.current = true // terminal
          onCompleteRef.current?.(data)
          return
        }
      }
      scheduleNext(intervalMs)
    }

    const onVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        !cancelled &&
        mountedRef.current &&
        !stoppedRef.current
      ) {
        // Reanudar ya, pero cancelando el timer en espera primero: si el poll de fondo
        // ya estaba agendado, dispararlo junto con este encadenaría dos polls concurrentes.
        scheduleNext(0)
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    tick() // primer fetch inmediato

    return () => {
      cancelled = true
      clearTimer()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [headerId, enabled, intervalMs, failThreshold, restartSignal, clearTimer])

  const refresh = useCallback(() => {
    if (!headerId) return
    getImportProgress(headerId).then((res) => {
      if (!mountedRef.current) return
      if (!res.didError && res.model) {
        setProgress(res.model)
        setConnectionLost(false)
        failCountRef.current = 0
        if (res.model.is_complete) {
          setIsComplete(true)
          stoppedRef.current = true
          onCompleteRef.current?.(res.model)
        }
      } else if (res.code === NOT_FOUND_CODE) {
        setNotFound(true)
        stoppedRef.current = true
      } else {
        failCountRef.current += 1
        if (failCountRef.current >= failThreshold) setConnectionLost(true)
      }
    })
  }, [headerId, failThreshold])

  return { progress, loading, connectionLost, isComplete, notFound, error, refresh }
}
